/**
 * /settings/tally-mappings — Settings → Accounting → Tally ledger
 * mappings (CR0.2 — Tally XML export).
 *
 * Per-ledger TallyPrime name overrides for the two Tally exports
 * (accounting vouchers + payroll JV). Two sections:
 *   1. Payroll ledgers  — kind="pseudo" rows (PAYROLL_* pseudo-ledgers
 *      that only exist for the payroll JV export)
 *   2. Chart of accounts — kind="account" rows
 *
 * Each row is an inline editor: an Input seeded from the EFFECTIVE
 * tally_ledger_name (mapping if set, else default) + a per-row Save.
 * Saving an empty input CLEARS the mapping — the backend reverts to the
 * default name and returns the resolved row, which reseeds the row's
 * local state. Follows the tax-codes settings page structure
 * (SettingsPageHeader + SettingsShell + SETTINGS_GROUPS; HR-admin
 * write-gate with a read-only Alert for everyone else).
 *
 * Wave 2: the payroll JV export is a PAYROLL feature, so with the
 * Accounting module OFF the page reads / writes the payroll pseudo-ledgers
 * through /api/v1/payroll/tally-mappings (payroll role, no accounting
 * gate) and shows only the "Payroll ledgers" section.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRightLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useFeatures } from "@/lib/auth/auth-context";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import {
  useTallyMappings,
  useUpsertTallyMapping,
} from "@/hooks/use-accounting";
import {
  usePayrollTallyMappings,
  useUpsertPayrollTallyMapping,
} from "@/hooks/use-books-posting";
import type {
  TallyMappingRow,
  UpsertTallyMappingRequest,
} from "@/types/accounting";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { useBelowSm } from "@/lib/use-below-sm";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/tally-mappings",
)({
  component: SettingsTallyMappingsRoute,
});

/** Stable per-row key — pseudo rows have no account_id and vice versa. */
function rowKey(row: TallyMappingRow): string {
  return row.kind === "pseudo"
    ? `pseudo:${row.pseudo_code}`
    : `account:${row.account_id}`;
}

/**
 * One inline-editable mapping row. Owns its Input state + its own
 * mutation instance so `isPending` (and the Save disable) is per-row —
 * saving one ledger never locks the other 29.
 */
function MappingRow({
  row,
  readOnly,
  payrollOnly,
  asCard = false,
}: Readonly<{
  row: TallyMappingRow;
  readOnly: boolean;
  payrollOnly: boolean;
  asCard?: boolean;
}>) {
  // Baseline = the last server-confirmed effective name. Seeded from
  // the list fetch; replaced by the resolved row every save so the
  // dirty check survives the list-invalidation refetch without flicker.
  const [baseline, setBaseline] = useState(row.tally_ledger_name);
  const [value, setValue] = useState(row.tally_ledger_name);
  const acctMut = useUpsertTallyMapping();
  const payrollMut = useUpsertPayrollTallyMapping();
  const mut = payrollOnly ? payrollMut : acctMut;
  const dirty = value !== baseline;

  const identity =
    row.kind === "pseudo" ? (row.label ?? row.pseudo_code ?? "—") : (row.account_name ?? "—");
  const subCode = row.kind === "pseudo" ? row.pseudo_code : row.account_code;

  async function onSave() {
    const trimmed = value.trim();
    const body: UpsertTallyMappingRequest =
      row.kind === "account"
        ? { account_id: row.account_id, tally_ledger_name: trimmed }
        : { pseudo_code: row.pseudo_code, tally_ledger_name: trimmed };
    try {
      const updated = payrollOnly
        ? await payrollMut.mutateAsync({
            pseudo_code: row.pseudo_code ?? "",
            tally_ledger_name: trimmed,
          })
        : await acctMut.mutateAsync(body);
      // Reseed from the RESOLVED row — after a clear the server sends
      // back the default name, so the input never sits empty.
      setBaseline(updated.tally_ledger_name);
      setValue(updated.tally_ledger_name);
      toast.success(
        trimmed === ""
          ? `${identity} reverted to its default ledger name.`
          : `${identity} will export as “${updated.tally_ledger_name}”.`,
      );
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const identityBlock = (
    <div className="min-w-0">
      <p className="text-sm font-medium">{identity}</p>
      {subCode && (
        <p className="mt-0.5 font-mono text-xs text-muted-foreground">
          {subCode}
        </p>
      )}
    </div>
  );
  const nameInput = (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      disabled={readOnly || mut.isPending}
      placeholder={row.default_name}
      aria-label={`Tally ledger name for ${identity}`}
      className="max-w-md"
    />
  );
  const defaultHint = (
    <p className="mt-1 text-xs text-muted-foreground">
      Default: {row.default_name}
    </p>
  );
  const statusBadge = (
    <StatusBadge
      tone={row.is_mapped ? "success" : "neutral"}
      label={row.is_mapped ? "Mapped" : "Default"}
    />
  );
  const saveButton = (
    <Button
      size="sm"
      variant="outline"
      className="gap-1.5"
      onClick={onSave}
      disabled={!dirty || mut.isPending}
    >
      {mut.isPending ? (
        <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Save aria-hidden className="h-3.5 w-3.5" />
      )}
      Save
    </Button>
  );

  // Phones: a stacked card — ledger + status, full-width name, full-width Save.
  if (asCard) {
    return (
      <li className="space-y-2 rounded-md border p-3">
        <div className="flex items-start justify-between gap-2">
          {identityBlock}
          <span className="shrink-0">{statusBadge}</span>
        </div>
        <div>
          {nameInput}
          {defaultHint}
        </div>
        {!readOnly && <div className="grid [&>*]:w-full">{saveButton}</div>}
      </li>
    );
  }

  return (
    <TableRow>
      <TableCell className="align-top">{identityBlock}</TableCell>
      <TableCell className="align-top">
        {nameInput}
        {defaultHint}
      </TableCell>
      <TableCell className="align-top">{statusBadge}</TableCell>
      {!readOnly && (
        <TableCell className="align-top text-right">{saveButton}</TableCell>
      )}
    </TableRow>
  );
}

function MappingSection({
  title,
  description,
  rows,
  readOnly,
  payrollOnly = false,
}: Readonly<{
  title: string;
  description: string;
  rows: TallyMappingRow[];
  readOnly: boolean;
  payrollOnly?: boolean;
}>) {
  const phone = useBelowSm();
  if (rows.length === 0) return null;
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {phone ? (
        <ul className="space-y-3">
          {rows.map((row) => (
            <MappingRow
              key={rowKey(row)}
              row={row}
              readOnly={readOnly}
              payrollOnly={payrollOnly}
              asCard
            />
          ))}
        </ul>
      ) : (
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-64">Ledger</TableHead>
              <TableHead>Tally Ledger Name</TableHead>
              <TableHead className="w-24">Status</TableHead>
              {!readOnly && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <MappingRow
                key={rowKey(row)}
                row={row}
                readOnly={readOnly}
                payrollOnly={payrollOnly}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      )}
    </section>
  );
}

function SettingsTallyMappingsRoute() {
  const { isHrAdmin, role } = useHrRoles();
  const booksOn = useFeatures().has("accounting");
  // With Accounting on the accountant edits the mappings too (PUT
  // /accounting/tally-mappings is accountingRole server-side); the
  // payroll-only endpoint (Accounting off) stays HR-admin.
  const canEdit = isHrAdmin || (booksOn && role === "accountant");
  // Accounting on → every ledger (CoA + payroll); off → payroll ledgers
  // only, through the payroll endpoint (no accounting gate).
  const acctQ = useTallyMappings(booksOn);
  const payrollQ = usePayrollTallyMappings(!booksOn);
  const q = booksOn ? acctQ : payrollQ;
  const payrollOnly = !booksOn;

  const items = q.data?.items ?? [];
  const pseudoRows = items.filter((r) => r.kind === "pseudo");
  const accountRows = items.filter((r) => r.kind === "account");

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Tally Ledger Mappings</h2>
            <p className="text-sm text-muted-foreground">
              The Tally exports use these EXACT ledger names — a Tally
              import fails or mis-posts if a name doesn&apos;t exactly
              match an existing ledger in TallyPrime (we never auto-create
              masters). Override a name here when your Tally books use a
              different ledger name; leave blank to use the default.
            </p>
          </div>

          {!canEdit && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only company admins, HR managers and accountants can
                change Tally ledger names.
              </AlertDescription>
            </Alert>
          )}

          {/* Loading skeletons */}
          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {/* Error */}
          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load Tally mappings</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {/* Empty state */}
          {q.data && items.length === 0 && (
            <EmptyState
              icon={ArrowRightLeft}
              title="No Mappable Ledgers"
              description="Once your chart of accounts is seeded, every account (plus the payroll pseudo-ledgers) appears here with its Tally ledger name."
            />
          )}

          {payrollOnly && (
            <Alert>
              <AlertTitle>Payroll ledgers only</AlertTitle>
              <AlertDescription>
                The Accounting module is off, so only the ledgers of the
                payroll journal-voucher export are shown. Chart-of-accounts
                ledger names appear here when Accounting is switched on.
              </AlertDescription>
            </Alert>
          )}

          {/* Sections */}
          {q.data && items.length > 0 && (
            <div className="space-y-8">
              <MappingSection
                title="Payroll Ledgers"
                description="Ledger names used by the payroll journal-voucher export. These pseudo-ledgers exist only for the Tally export — they are not part of your chart of accounts. When a month charges Professional Tax in more than one state, the voucher has one PT line per state named after the PT ledger plus the state code (for example “Professional Tax Payable - KA”), so create one Tally ledger per state with those names."
                rows={pseudoRows}
                readOnly={!canEdit}
                payrollOnly={payrollOnly}
              />
              <MappingSection
                title="Chart of Accounts"
                description="Ledger names used by the accounting voucher export, one per account in your chart of accounts."
                rows={accountRows}
                readOnly={!canEdit}
              />
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
